import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { OptionChips } from '../../components/OptionChips';
import { PageHeader } from '../../components/PageHeader';
import {
  PLAN_COUNT_LIMIT,
  PLAN_FEATURE_LABEL,
  PLAN_LABEL,
  PLAN_PRICE,
  planIncludedFeatures,
} from '../../components/plan/planLabels';
import { usePlan } from '../../components/plan/usePlan';
import { ApiError } from '../../lib/apiClient';
import { closeCheckout, loadPaddle, openCheckout, type PaddleEvent } from '../../lib/paddle';
import type { BillingInterval, BillingPlan, PlanName } from '../../types/api';
import { useAccount } from '../onboarding/useAccount';
import {
  billingErrorMessage,
  changePlan,
  fetchSubscription,
  isLiveSubscription,
  startCheckout,
} from './billingApi';
import { useBilling } from './useBilling';

const AGENCY_EMAIL = 'info@thekaizenglobal.com';

/** How often, and how long, to wait for Paddle's webhook after a payment. */
const POLL_EVERY_MS = 2000;
const POLL_FOR_MS = 30_000;

const INTERVAL_OPTIONS = [
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly', detail: '2 months free' },
] as const;

type Phase =
  | { kind: 'idle' }
  | { kind: 'opening'; plan: BillingPlan }
  | { kind: 'checkout'; plan: BillingPlan }
  | { kind: 'finishing' }
  | { kind: 'done'; plan: BillingPlan }
  | { kind: 'timeout' };

function isBillingPlan(value: string | null): value is BillingPlan {
  return value === 'solo' || value === 'studio';
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** "40 posts a month", "Unlimited brands", "1 team member". */
function countLine(value: number | null, one: string, many: string): string {
  if (value === null) return `Unlimited ${many}`;
  return `${value.toLocaleString()} ${value === 1 ? one : many}`;
}

/**
 * `/upgrade`: the plans side by side, and the way onto one.
 *
 * Without a live subscription a plan's button makes a server-side Paddle
 * transaction (`POST /billing/checkout`, the browser sends only plan and
 * interval) and opens Paddle's overlay on it. On `checkout.completed` the page
 * polls the subscription until Paddle's webhook has landed, then re-reads the
 * plan. With a live subscription the button switches plans after a confirm
 * (prorated now). Owner only: a member reads the plans and is told to ask the
 * owner. Billing off (or its config unreadable) shows the plans with "Billing
 * opens soon" and no buttons.
 */
export function UpgradePage() {
  const { entitlements, refresh } = usePlan();
  const { business } = useAccount();
  const isOwner = business?.role === 'owner';
  const { config, subscription, setSubscription, reload } = useBilling(isOwner);
  const [searchParams] = useSearchParams();
  const pickedParam = searchParams.get('plan');
  const picked = isBillingPlan(pickedParam) ? pickedParam : null;

  // Follows the live subscription until the user picks.
  const [chosenInterval, setChosenInterval] = useState<BillingInterval | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [error, setError] = useState<string | null>(null);
  const [switchTo, setSwitchTo] = useState<BillingPlan | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const interval: BillingInterval = chosenInterval ?? subscription?.interval ?? 'month';
  const live = isLiveSubscription(subscription?.status);
  const canBuy = config?.enabled === true && isOwner;
  const isBusy =
    phase.kind === 'opening' || phase.kind === 'checkout' || phase.kind === 'finishing';

  /** Waits for the webhook to make the subscription live, then re-reads the plan. */
  async function finishCheckout(plan: BillingPlan) {
    setPhase({ kind: 'finishing' });
    for (let polls = 0; polls < POLL_FOR_MS / POLL_EVERY_MS; polls += 1) {
      await wait(POLL_EVERY_MS);
      if (!isMounted.current) return;
      try {
        const fresh = await fetchSubscription();
        if (!isMounted.current) return;
        if (isLiveSubscription(fresh.status)) {
          setSubscription(fresh);
          refresh();
          setPhase({ kind: 'done', plan: fresh.plan ?? plan });
          return;
        }
      } catch {
        // A blip while Paddle's webhook is on its way; keep asking until the polls run out.
      }
    }
    if (isMounted.current) setPhase({ kind: 'timeout' });
  }

  async function startPurchase(plan: BillingPlan) {
    if (!config) return;
    setError(null);
    setPhase({ kind: 'opening', plan });
    let completed = false;
    const onEvent = (event: PaddleEvent) => {
      if (event.name === 'checkout.completed' && !completed) {
        completed = true;
        closeCheckout();
        void finishCheckout(plan);
      } else if (event.name === 'checkout.closed' && !completed && isMounted.current) {
        setPhase({ kind: 'idle' });
      }
    };
    try {
      const [{ transaction_id }] = await Promise.all([
        startCheckout({ plan, interval }),
        loadPaddle(config),
      ]);
      if (!isMounted.current) return;
      setPhase({ kind: 'checkout', plan });
      await openCheckout(transaction_id, onEvent);
    } catch (caught) {
      if (!isMounted.current) return;
      setError(billingErrorMessage(caught, 'Checkout did not open. Try again.'));
      setPhase({ kind: 'idle' });
      // Paid in another tab: read the subscription so the buttons become switches.
      if (caught instanceof ApiError && caught.code === 'ALREADY_SUBSCRIBED') reload();
    }
  }

  async function confirmSwitch(plan: BillingPlan) {
    setIsSwitching(true);
    setSwitchError(null);
    try {
      const fresh = await changePlan({ plan, interval });
      setSubscription(fresh);
      refresh();
      setSwitchTo(null);
      setError(null);
      setPhase({ kind: 'done', plan: fresh.plan ?? plan });
    } catch (caught) {
      setSwitchError(billingErrorMessage(caught, 'The switch did not go through. Try again.'));
    } finally {
      setIsSwitching(false);
    }
  }

  function planAction(plan: BillingPlan) {
    if (!config || !isOwner) return null;
    if (!config.enabled) return <p className="field__hint">Billing opens soon</p>;
    const label = PLAN_LABEL[plan];

    if (live && subscription) {
      if (subscription.plan === plan && subscription.interval === interval) {
        return <p className="upgrade-plan__current">Your plan</p>;
      }
      const text =
        subscription.plan === plan
          ? `Switch to ${interval === 'year' ? 'yearly' : 'monthly'}`
          : `Switch to ${label}`;
      return (
        <button
          className="button"
          type="button"
          disabled={isBusy}
          onClick={() => {
            setSwitchError(null);
            setSwitchTo(plan);
          }}
        >
          {text}
        </button>
      );
    }

    const isOpening = phase.kind === 'opening' && phase.plan === plan;
    return (
      <button
        className="button button--primary"
        type="button"
        disabled={isBusy}
        onClick={() => void startPurchase(plan)}
      >
        {isOpening ? 'Opening checkout' : `Choose ${label}`}
      </button>
    );
  }

  function column(plan: Exclude<PlanName, 'agency'>) {
    const isCurrent = entitlements?.plan === plan;
    const features = planIncludedFeatures(plan);
    const classes = ['upgrade-plan'];
    if (picked === plan) classes.push('upgrade-plan--picked');

    return (
      <section className={classes.join(' ')} key={plan} aria-labelledby={`upgrade-${plan}`}>
        <div className="upgrade-plan__head">
          <h2 className="upgrade-plan__name" id={`upgrade-${plan}`}>
            {PLAN_LABEL[plan]}
          </h2>
          {isCurrent ? (
            <span className="badge badge--neutral">
              {entitlements.trial_state === 'active' ? 'Trial' : 'Current'}
            </span>
          ) : null}
        </div>
        <p className="upgrade-plan__price">
          {plan === 'free' ? (
            '$0'
          ) : (
            <>
              ${PLAN_PRICE[plan][interval]}
              <span className="upgrade-plan__per"> / {interval}</span>
            </>
          )}
        </p>
        {/* The action sits under the price so it is in view on a short laptop. */}
        {plan === 'free' ? null : <div className="upgrade-plan__action">{planAction(plan)}</div>}
        <ul className="upgrade-plan__list">
          <li>{countLine(PLAN_COUNT_LIMIT.posts[plan], 'post a month', 'posts a month')}</li>
          <li>{countLine(PLAN_COUNT_LIMIT.brands[plan], 'brand', 'brands')}</li>
          <li>{countLine(PLAN_COUNT_LIMIT.team_members[plan], 'team member', 'team members')}</li>
          {features.map((feature) => (
            <li key={feature}>{PLAN_FEATURE_LABEL[feature]}</li>
          ))}
        </ul>
      </section>
    );
  }

  return (
    <main className="page">
      <PageHeader
        section="Account"
        title="Plans"
        lede="Prices in US dollars. Sales tax is added at checkout where it applies."
      />

      {!isOwner ? (
        <p className="upsell" role="note">
          Ask the brand owner to change the plan.
        </p>
      ) : null}

      <OptionChips
        legend="Billing"
        hint={canBuy ? 'Discount codes go in the payment window.' : undefined}
        mode="single"
        name="upgrade-interval"
        options={INTERVAL_OPTIONS}
        selected={[interval]}
        onToggle={(value) => setChosenInterval(value === 'year' ? 'year' : 'month')}
        disabled={isBusy}
      />

      <div className="upgrade-status" role="status" aria-live="polite">
        {phase.kind === 'finishing' ? <p>Finishing up…</p> : null}
        {phase.kind === 'done' ? <p>You&rsquo;re on {PLAN_LABEL[phase.plan]}.</p> : null}
        {phase.kind === 'timeout' ? (
          <p>
            Payment received; your plan updates in a minute.{' '}
            <button
              className="button button--ghost"
              type="button"
              onClick={() => {
                setPhase({ kind: 'idle' });
                reload();
                refresh();
              }}
            >
              Refresh
            </button>
          </p>
        ) : null}
      </div>

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="upgrade-plans">
        {column('free')}
        {column('solo')}
        {column('studio')}
        <section className="upgrade-plan" aria-labelledby="upgrade-agency">
          <div className="upgrade-plan__head">
            <h2 className="upgrade-plan__name" id="upgrade-agency">
              {PLAN_LABEL.agency}
            </h2>
            {entitlements?.plan === 'agency' ? (
              <span className="badge badge--neutral">Current</span>
            ) : null}
          </div>
          <p className="upgrade-plan__price">Custom</p>
          <div className="upgrade-plan__action">
            <a className="button" href={`mailto:${AGENCY_EMAIL}?subject=Autopostmate%20Agency`}>
              Talk to us
            </a>
          </div>
          <ul className="upgrade-plan__list">
            <li>Unlimited brands and team members</li>
            <li>Custom limits</li>
            <li>Everything in {PLAN_LABEL.studio}</li>
          </ul>
        </section>
      </div>

      {switchTo ? (
        <ConfirmDialog
          title={`Switch to ${PLAN_LABEL[switchTo]} ${interval === 'year' ? 'yearly' : 'monthly'}?`}
          message={`${PLAN_LABEL[switchTo]} is $${PLAN_PRICE[switchTo][interval]} a ${interval}. The change starts now, and the prorated difference is charged or credited straight away.`}
          confirmLabel="Switch plan"
          busyLabel="Switching"
          dismissLabel="Not now"
          isBusy={isSwitching}
          error={switchError}
          onConfirm={() => void confirmSwitch(switchTo)}
          onDismiss={() => setSwitchTo(null)}
        />
      ) : null}
    </main>
  );
}
